/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Hook and context for chapter tree expand/collapse state.
 *
 * @file useChapterTreeExpansion.js
 * @author Gladson Samuel S
 * @since 2.0.0
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import { expandAncestorsOfSelected } from './chapterContentTreeUtils';

const ChapterTreeExpansionContext = createContext(null);

export const ChapterTreeExpansionProvider = ({ value, children }) => (
  <ChapterTreeExpansionContext.Provider value={value}>
    {children}
  </ChapterTreeExpansionContext.Provider>
);

ChapterTreeExpansionProvider.propTypes = {
  value: PropTypes.shape({
    expandedNodes: PropTypes.object.isRequired,
    selectedNodes: PropTypes.array,
    handleToggleExpanded: PropTypes.func.isRequired,
    handleToggleNode: PropTypes.func.isRequired
  }).isRequired,
  children: PropTypes.node.isRequired
};

export const useChapterTreeExpansionContext = () => {
  const context = useContext(ChapterTreeExpansionContext);

  if (!context) {
    throw new Error('useChapterTreeExpansionContext must be used within ChapterTreeExpansionProvider');
  }

  return context;
};

export const useChapterTreeExpansion = ({ nodes, selectedNodes, onToggleNode }) => {
  const [expandedNodes, setExpandedNodes] = useState({});
  const isCheckboxClickRef = useRef(false);

  useEffect(() => {
    if (isCheckboxClickRef.current) {
      isCheckboxClickRef.current = false;

      return;
    }

    expandAncestorsOfSelected(nodes, selectedNodes, setExpandedNodes);
  }, [selectedNodes, nodes]);

  const handleToggleNode = (node) => {
    isCheckboxClickRef.current = true;
    onToggleNode(node);
  };

  const handleToggleExpanded = (nodeId) => {
    setExpandedNodes((prev) => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };

  return {
    expandedNodes,
    selectedNodes,
    handleToggleExpanded,
    handleToggleNode
  };
};
